import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, FileText, Image as ImageIcon, Trash2, Download, Star,
  ChevronDown, ChevronRight, Building2, Layers, Loader2, Sparkles,
  AlertCircle, CheckCircle2, FileCheck2, Inbox, BookmarkCheck, Wand2,
  Crop,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Slider } from '../ui/slider';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import PlanCropperDialog from './PlanCropperDialog';
import { FileFoldersPanel } from './FileFoldersPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Doc type labels & colors
const DOC_TYPE_META = {
  cctp:         { label: 'CCTP',          color: '#A855F7', icon: FileCheck2 },
  plan_lot:     { label: 'Plan lot',      color: '#3B82F6', icon: Layers },
  plan_etage:   { label: "Plan d'étage",  color: '#6366F1', icon: Layers },
  facade:       { label: 'Façade',        color: '#10B981', icon: Building2 },
  coupe:        { label: 'Coupe',         color: '#0EA5E9', icon: Layers },
  plan_masse:   { label: 'Plan masse',    color: '#F59E0B', icon: Layers },
  piece_ecrite: { label: 'Pièce écrite',  color: '#6B7280', icon: FileText },
  photo:        { label: 'Photo / Rendu 3D', color: '#EC4899', icon: ImageIcon },
  other:        { label: 'Autre',         color: '#64748B', icon: FileText },
};
const DOC_TYPES_LIST = Object.keys(DOC_TYPE_META);
// V16.4 — champs de rattachement pertinents par type (le reste est masqué : une photo 3D ou un plan masse ne se rattache pas)
const TYPE_FIELDS = { plan_lot: ['building', 'floor', 'lot'], plan_etage: ['building', 'floor'], facade: ['building'], coupe: ['building'] };
const fieldsFor = (dt) => TYPE_FIELDS[dt] || [];
const TYPE_HINT = { photo: 'Référence d\u2019ambiance — pas de rattachement', plan_masse: 'Projet entier — pas de rattachement', cctp: 'Document projet', piece_ecrite: 'Document projet' };

function confidenceTone(score) {
  if (score == null) return { color: '#64748B', label: '—' };
  if (score >= 0.90) return { color: '#10B981', label: `${Math.round(score * 100)}%` };
  if (score >= 0.70) return { color: '#D9A05B', label: `${Math.round(score * 100)}%` };
  return { color: '#F97316', label: `${Math.round(score * 100)}%` };
}

// Bulk candidates: CCTP + Plan lot ≥ threshold confidence, not yet retained
function inputFilesForBulk(files, threshold = 0.9) {
  return files.filter(
    (f) =>
      !f.is_retained &&
      ['cctp', 'plan_lot'].includes(f.doc_type) &&
      typeof f.confidence_score === 'number' &&
      f.confidence_score >= threshold
  );
}

const ProjectFilesWorkspace = ({
  projectId,
  files,
  setFiles,
  buildings,
  floorsByBuilding,
  allLots,
  generations,
  loadingGenerations,
  onRefresh,
  onOpenStudio,
}) => {
  const [zone, setZone] = useState('inputs'); // inputs | retained | outputs
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // file obj
  const [expandedBuildings, setExpandedBuildings] = useState({});
  const [pollTick, setPollTick] = useState(0);

  // ---- Asset Pipeline (Iter 63) : cropper modal state ----
  // `cropperState` shape: { file, lot } — the file to rasterize and the lot
  // to attach the normalized plan to. `null` = closed.
  const [cropperState, setCropperState] = useState(null);
  const [cropperImageUrl, setCropperImageUrl] = useState(null);
  // De-dupe auto-prompts so we don't re-open on every re-render
  const [autoPromptedFileIds, setAutoPromptedFileIds] = useState(() => new Set());
  // V16.4 — dossiers libres, vue (bâtiment / type) et suggestions de rattachement
  const [fileFolders, setFileFolders] = useState([]);
  const [folderSel, setFolderSel] = useState('root');
  const [groupView, setGroupView] = useState('building');
  const loadFolders = useCallback(async () => { try { const r = await axios.get(`${API}/projects/${projectId}/file-folders`, { withCredentials: true }); setFileFolders(r.data); } catch { /* silencieux */ } }, [projectId]);
  useEffect(() => { loadFolders(); }, [loadFolders]);
  const folderOps = {
    create: async (name, parent) => { try { await axios.post(`${API}/projects/${projectId}/file-folders`, { name, parent_id: parent }, { withCredentials: true }); loadFolders(); } catch { toast.error('Création impossible'); } },
    rename: async (f, name) => { try { await axios.put(`${API}/projects/${projectId}/file-folders/${f.folder_id}`, { name, parent_id: f.parent_id || null }, { withCredentials: true }); loadFolders(); } catch { toast.error('Renommage impossible'); } },
    remove: async (f) => { if (!window.confirm(`Supprimer le dossier « ${f.name} » et ses sous-dossiers ? Les fichiers restent dans le projet.`)) return; try { await axios.delete(`${API}/projects/${projectId}/file-folders/${f.folder_id}`, { withCredentials: true }); if (folderSel === f.folder_id) setFolderSel('root'); loadFolders(); setFiles((prev) => prev.map((x) => (x.folder_id === f.folder_id ? { ...x, folder_id: null } : x))); } catch { toast.error('Suppression impossible'); } },
    drop: async (fileId, folderId) => { try { await axios.put(`${API}/files/${fileId}`, { folder_id: folderId }, { withCredentials: true }); setFiles((prev) => prev.map((x) => (x.file_id === fileId ? { ...x, folder_id: folderId } : x))); loadFolders(); toast.success(folderId ? 'Fichier rangé' : 'Fichier sorti du dossier'); } catch { toast.error('Déplacement impossible'); } },
  };
  const applySuggestion = async (file) => {
    try { const r = await axios.post(`${API}/files/${file.file_id}/apply-suggestion`, {}, { withCredentials: true }); setFiles((prev) => prev.map((x) => (x.file_id === file.file_id ? { ...x, ...r.data.applied, suggestion: null } : x))); toast.success('Rattachement appliqué'); } catch { toast.error('Application impossible'); }
  };
  const suggestAll = async () => {
    try { const r = await axios.post(`${API}/projects/${projectId}/files/suggest-attribution`, {}, { withCredentials: true }); toast.success(`${r.data.suggested} suggestion(s)`); const f = await axios.get(`${API}/projects/${projectId}/files`, { withCredentials: true }); setFiles(f.data); } catch { toast.error('Analyse impossible'); }
  };
  const applyAllSuggestions = async (list) => { for (const f of list) await applySuggestion(f); };

  /**
   * Loads the source image (rasterized PDF page if needed) and opens the cropper.
   * The lot is resolved from the file (via lot_id, or via allLots.find by
   * selected_plan_file_id). Returns silently if no lot can be inferred.
   */
  const openCropperForFile = useCallback(async (file) => {
    // Find target lot
    let lot = null;
    if (file.lot_id) lot = allLots.find((l) => l.lot_id === file.lot_id) || null;
    if (!lot) lot = allLots.find((l) => l.selected_plan_file_id === file.file_id) || null;
    if (!lot) {
      toast.info('Assigne d\'abord ce plan à un lot pour le recadrer');
      return;
    }
    // Build a frontend URL to the rasterized preview endpoint (always returns PNG, even for PDFs)
    const previewUrl = `${API}/files/${file.file_id}/raster-preview?dpi=200`;
    // Try to authenticate via cookie — we need to fetch as Blob then revoke
    try {
      const r = await axios.get(previewUrl, { withCredentials: true, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(r.data);
      setCropperImageUrl(blobUrl);
      setCropperState({ file, lot });
    } catch (e) {
      toast.error('Impossible de charger le plan pour le recadrage');
    }
  }, [allLots]);

  const closeCropper = () => {
    if (cropperImageUrl) URL.revokeObjectURL(cropperImageUrl);
    setCropperImageUrl(null);
    setCropperState(null);
  };

  /**
   * Final apply : the dialog already produced a cropped+filtered PNG Blob.
   * We POST it to the backend (mode 2 — client rasterization) to persist as
   * `lot.normalized_plan_image_url`.
   */
  const applyCropToLot = async ({ blob, dataUrl, width, height, croppedAreaPixels, rotation, filters }) => {
    if (!cropperState?.lot || !cropperState?.file) return;
    try {
      const payload = {
        source_file_id: cropperState.file.file_id,
        crop: croppedAreaPixels
          ? { x: croppedAreaPixels.x, y: croppedAreaPixels.y, width: croppedAreaPixels.width, height: croppedAreaPixels.height }
          : null,
        filters,
        cropped_image_base64: dataUrl,
      };
      await axios.post(
        `${API}/lots/${cropperState.lot.lot_id}/normalize-plan`,
        payload,
        { withCredentials: true },
      );
      toast.success(`Plan normalisé pour ${cropperState.lot.lot_number} (${width}×${height})`);
      closeCropper();
      onRefresh?.();
    } catch (e) {
      toast.error('Erreur upload plan normalisé : ' + (e?.response?.data?.detail || e.message));
    }
  };

  // Auto-poll for queued/processing categorization
  useEffect(() => {
    const hasPending = files.some(
      (f) => f.categorization_status === 'queued' || f.categorization_status === 'processing'
    );
    if (!hasPending) return;
    const id = setTimeout(() => setPollTick((t) => t + 1), 2500);
    return () => clearTimeout(id);
  }, [files, pollTick]);

  useEffect(() => {
    if (pollTick > 0) onRefresh?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollTick]);

  // Upload via dropzone
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles?.length) return;
      setUploading(true);
      try {
        for (const file of acceptedFiles) {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(`${API}/projects/${projectId}/files`, fd, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
        toast.success(`${acceptedFiles.length} fichier(s) uploadé(s). IA en cours…`);
        onRefresh?.();
      } catch (e) {
        toast.error('Erreur lors de l\'upload');
      } finally {
        setUploading(false);
      }
    },
    [projectId, onRefresh]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  // Update file (doc_type / is_retained / building_id / floor_id / lot_id)
  const updateFile = async (file_id, patch, opts = {}) => {
    try {
      const { data } = await axios.put(`${API}/files/${file_id}`, patch, { withCredentials: true });
      setFiles((prev) => prev.map((f) => (f.file_id === file_id ? { ...f, ...data } : f)));
      if (!opts.silent) toast.success('Mis à jour');

      // Auto-prompt cropper when user classifies a file as "plan_lot"
      // (Asset Pipeline, Iter 63). De-duped via `autoPromptedFileIds`.
      if (patch.doc_type === 'plan_lot' && !autoPromptedFileIds.has(file_id)) {
        setAutoPromptedFileIds((s) => new Set([...s, file_id]));
        // Defer slightly to let the UI settle + give time for lot inference
        setTimeout(() => openCropperForFile({ ...data, file_id }), 400);
      }
    } catch (e) {
      toast.error('Erreur mise à jour');
    }
  };

  const recategorize = async (file_id) => {
    try {
      await axios.post(`${API}/files/${file_id}/categorize`, {}, { withCredentials: true });
      toast.info('Nouvelle analyse IA lancée…');
      onRefresh?.();
    } catch {
      toast.error('Erreur catégorisation');
    }
  };

  // Bulk promote: count candidates + perform action
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkThreshold, setBulkThreshold] = useState(0.9); // 0.60 - 1.00
  const bulkCandidates = useMemo(
    () => inputFilesForBulk(files, bulkThreshold),
    [files, bulkThreshold]
  );
  const runBulkPromote = async () => {
    if (bulkCandidates.length === 0) return;
    setBulkRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/files/bulk-retain`,
        { min_confidence: bulkThreshold, doc_types: ['cctp', 'plan_lot'] },
        { withCredentials: true }
      );
      toast.success(`${data.updated} fichier(s) promu(s) vers Documents retenus`);
      onRefresh?.();
    } catch (e) {
      toast.error('Erreur promotion en masse');
    } finally {
      setBulkRunning(false);
    }
  };

  const deleteFile = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/files/${confirmDelete.file_id}`, { withCredentials: true });
      setFiles((prev) => prev.filter((f) => f.file_id !== confirmDelete.file_id));
      toast.success('Fichier supprimé');
    } catch {
      toast.error('Erreur suppression');
    } finally {
      setConfirmDelete(null);
    }
  };

  // Group files by building → floor for tree view
  const inFolder = useCallback((f) => (folderSel === 'root' ? true : folderSel === 'refs' ? f.doc_type === 'photo' : f.folder_id === folderSel), [folderSel]);
  const tree = useMemo(() => {
    const t = {};
    // pseudo-building "unassigned"
    for (const f of files.filter(inFolder)) {
      const bid = groupView === 'type' ? (f.doc_type || 'other') : (f.building_id || '__unassigned__');
      const flid = groupView === 'type' ? '__unfloored__' : (f.floor_id || '__unfloored__');
      if (!t[bid]) t[bid] = {};
      if (!t[bid][flid]) t[bid][flid] = [];
      t[bid][flid].push(f);
    }
    return t;
  }, [files, inFolder, groupView]);
  const typeGroups = useMemo(() => DOC_TYPES_LIST.map((dt) => ({ building_id: dt, name: DOC_TYPE_META[dt].label })), []);

  // Initial expand state
  useEffect(() => {
    if (Object.keys(expandedBuildings).length === 0 && buildings.length) {
      setExpandedBuildings(buildings.reduce((acc, b) => ({ ...acc, [b.building_id]: true }), { __unassigned__: true }));
    }
  }, [buildings, expandedBuildings]);

  const toggleBuilding = (bid) => setExpandedBuildings((p) => ({ ...p, [bid]: !p[bid] }));

  const inputFiles = files.filter((f) => !f.is_retained && inFolder(f));
  const suggestedFiles = files.filter((f) => inFolder(f) && f.suggestion);
  const retainedFiles = files.filter((f) => f.is_retained);

  const counts = {
    inputs: files.length,
    retained: retainedFiles.length,
    outputs: generations?.length || 0,
  };

  return (
    <div className="space-y-5" data-testid="project-files-workspace">
      {/* Zone selector pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { id: 'inputs', label: 'Inputs', icon: Inbox, count: counts.inputs },
          { id: 'retained', label: 'Documents retenus', icon: BookmarkCheck, count: counts.retained },
          { id: 'outputs', label: 'Outputs (générations)', icon: Sparkles, count: counts.outputs },
        ].map((z) => (
          <button
            key={z.id}
            onClick={() => setZone(z.id)}
            data-testid={`zone-pill-${z.id}`}
            className="flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors"
            style={{
              background: zone === z.id ? 'rgba(217,160,91,0.18)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${zone === z.id ? 'rgba(217,160,91,0.55)' : 'rgba(255,255,255,0.10)'}`,
              color: zone === z.id ? '#E8B26C' : 'rgba(255,255,255,0.65)',
            }}
          >
            <z.icon className="w-3.5 h-3.5" />
            {z.label}
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full tabular-nums"
              style={{
                background: zone === z.id ? 'rgba(217,160,91,0.25)' : 'rgba(255,255,255,0.08)',
                color: zone === z.id ? '#E8B26C' : 'rgba(255,255,255,0.5)',
              }}
            >
              {z.count}
            </span>
          </button>
        ))}
      </div>

      {/* Upload zone (only for inputs/retained) */}
      {zone !== 'outputs' && (
        <div
          {...getRootProps()}
          data-testid="files-dropzone"
          className="flex items-center justify-between rounded-xl border-2 border-dashed p-4 transition-colors cursor-pointer"
          style={{
            borderColor: isDragActive ? 'rgba(217,160,91,0.7)' : 'rgba(255,255,255,0.12)',
            background: isDragActive ? 'rgba(217,160,91,0.06)' : 'rgba(255,255,255,0.02)',
            opacity: uploading ? 0.5 : 1,
            pointerEvents: uploading ? 'none' : 'auto',
          }}
        >
          <input {...getInputProps()} />
          <div className="flex items-center gap-3">
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#D9A05B' }} />
            ) : (
              <Upload className="w-5 h-5" style={{ color: '#D9A05B' }} />
            )}
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--c-text-1, #fff)' }}>
                {uploading ? 'Upload en cours…' : 'Glisser-déposer des fichiers ici'}
              </p>
              <p className="text-xs" style={{ color: 'var(--c-text-muted, rgba(255,255,255,0.5))' }}>
                PDF, images (PNG, JPG) · IA classera automatiquement
              </p>
            </div>
          </div>
          <Sparkles className="w-4 h-4" style={{ color: 'rgba(217,160,91,0.6)' }} />
        </div>
      )}

      {/* INPUTS zone — tree view */}
      {zone === 'inputs' && (
        <>
          {/* Bulk promote bar — always visible if at least 1 input file has confidence_score */}
          {files.some((f) => !f.is_retained && typeof f.confidence_score === 'number') && (
            <div
              className="px-3.5 py-2.5 rounded-xl"
              style={{
                background: 'linear-gradient(90deg, rgba(217,160,91,0.10) 0%, rgba(217,160,91,0.04) 100%)',
                border: '1px solid rgba(217,160,91,0.30)',
              }}
              data-testid="bulk-promote-bar"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5">
                  <Wand2 className="w-4 h-4" style={{ color: '#D9A05B' }} />
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--c-text-1, #fff)' }}>
                      <span className="tabular-nums" data-testid="bulk-candidate-count">{bulkCandidates.length}</span>{' '}
                      candidat{bulkCandidates.length > 1 ? 's' : ''} IA prêt{bulkCandidates.length > 1 ? 's' : ''} à promouvoir
                    </p>
                    <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.50)' }}>
                      CCTP + Plans avec confidence IA ≥ {Math.round(bulkThreshold * 100)}%
                    </p>
                  </div>
                </div>
                <Button
                  onClick={runBulkPromote}
                  disabled={bulkRunning || bulkCandidates.length === 0}
                  data-testid="bulk-promote-btn"
                  style={{ background: '#D9A05B', color: '#000' }}
                  className="hover:opacity-90 disabled:opacity-40"
                  size="sm"
                >
                  {bulkRunning ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-1.5" />
                  )}
                  Promouvoir vers Retenus
                </Button>
              </div>
              {/* Threshold slider */}
              <div className="mt-3 flex items-center gap-3" data-testid="bulk-threshold-control">
                <span className="text-[10px] uppercase tracking-wider shrink-0"
                  style={{ color: 'rgba(255,255,255,0.50)' }}>
                  Seuil
                </span>
                <Slider
                  value={[bulkThreshold * 100]}
                  onValueChange={(v) => setBulkThreshold(v[0] / 100)}
                  min={60}
                  max={100}
                  step={5}
                  className="flex-1 max-w-md"
                  data-testid="bulk-threshold-slider"
                />
                <span
                  className="tabular-nums text-xs font-semibold w-12 text-right"
                  style={{
                    color: bulkThreshold >= 0.9 ? '#10B981' : bulkThreshold >= 0.7 ? '#D9A05B' : '#F97316',
                  }}
                  data-testid="bulk-threshold-value"
                >
                  {Math.round(bulkThreshold * 100)}%
                </span>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap" data-testid="files-view-bar">
            <span className="inline-flex rounded-lg border border-[var(--c-border)] overflow-hidden">
              {[['building', 'Par bâtiment'], ['type', 'Par type']].map(([k, l]) => <button key={k} type="button" onClick={() => setGroupView(k)} data-testid={`files-view-${k}`} className={`h-8 px-3 text-[12px] ${groupView === k ? 'bg-[var(--c-gold-soft)] text-[var(--c-text)] font-medium' : 'text-[var(--c-text-2)] hover:bg-[var(--c-surface-2)]'}`}>{l}</button>)}
            </span>
            <button type="button" onClick={suggestAll} className="hud-btn hud-btn-secondary hud-btn-sm" data-testid="files-suggest-btn"><Sparkles className="w-3.5 h-3.5" />Analyser les rattachements</button>
            {suggestedFiles.length > 0 && <button type="button" onClick={() => applyAllSuggestions(suggestedFiles)} className="hud-btn hud-btn-primary hud-btn-sm" data-testid="files-apply-all-btn">Appliquer {suggestedFiles.length} suggestion{suggestedFiles.length > 1 ? 's' : ''}</button>}
          </div>
          <div className="flex gap-4 items-start">
          <FileFoldersPanel folders={fileFolders} selected={folderSel} onSelect={setFolderSel} onCreate={folderOps.create} onRename={folderOps.rename} onDelete={folderOps.remove} onDropFile={folderOps.drop}
            totalCount={files.length} refCount={files.filter((f) => f.doc_type === 'photo').length} />
          <div className="flex-1 min-w-0">
          <FilesTree
            tree={tree}
            buildings={groupView === 'type' ? typeGroups : buildings}
            floorsByBuilding={groupView === 'type' ? {} : floorsByBuilding}
            expandedBuildings={expandedBuildings}
            toggleBuilding={toggleBuilding}
            showRetainedOnly={false}
            allFiles={inputFiles}
            renderRow={(f) => (
              <FileRow
                key={f.file_id}
                file={f}
                allLots={allLots}
                buildings={buildings}
                floorsByBuilding={floorsByBuilding}
                onUpdate={updateFile}
                onRecategorize={recategorize}
                onDelete={(file) => setConfirmDelete(file)}
                onOpenCropper={openCropperForFile}
                onApplySuggestion={applySuggestion}
              />
            )}
          />
          </div>
          </div>
        </>
      )}

      {/* RETAINED zone — flat list of retained */}
      {zone === 'retained' && (
        <div className="space-y-2" data-testid="retained-list">
          {retainedFiles.length === 0 ? (
            <EmptyState icon={BookmarkCheck} label="Aucun document retenu" sub="Étoile un fichier dans Inputs pour le marquer comme officiel/retenu" />
          ) : (
            retainedFiles.map((f) => (
              <FileRow
                key={f.file_id}
                file={f}
                allLots={allLots}
                buildings={buildings}
                floorsByBuilding={floorsByBuilding}
                onUpdate={updateFile}
                onRecategorize={recategorize}
                onDelete={(file) => setConfirmDelete(file)}
                onOpenCropper={openCropperForFile}
              />
            ))
          )}
        </div>
      )}

      {/* OUTPUTS zone — generations grid grouped by building/lot */}
      {zone === 'outputs' && (
        <GenerationsGrid
          generations={generations}
          loading={loadingGenerations}
          allLots={allLots}
          buildings={buildings}
          projectId={projectId}
          onOpenStudio={onOpenStudio}
        />
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-file-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce fichier ?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium">{confirmDelete?.original_filename}</span> sera supprimé définitivement.
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-file">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteFile}
              data-testid="confirm-delete-file"
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Plan Cropper modal (Asset Pipeline, Iter 63) */}
      {cropperState && cropperImageUrl && (
        <PlanCropperDialog
          open
          imageUrl={cropperImageUrl}
          title={`Recadrer le plan — Lot ${cropperState.lot.lot_number}`}
          onCancel={closeCropper}
          onApply={applyCropToLot}
        />
      )}
    </div>
  );
};

// ---------- Tree view ----------
const FilesTree = ({ tree, buildings, floorsByBuilding, expandedBuildings, toggleBuilding, allFiles, renderRow }) => {
  if (allFiles.length === 0) {
    return <EmptyState icon={Inbox} label="Aucun fichier" sub="Glisse-dépose tes premiers documents" />;
  }
  const buildingsList = [
    ...buildings,
    { building_id: '__unassigned__', name: 'Non assigné' },
  ];
  return (
    <div className="space-y-2" data-testid="files-tree">
      {buildingsList.map((b) => {
        const byFloor = tree[b.building_id];
        if (!byFloor) return null;
        const floorCount = Object.values(byFloor).reduce((acc, arr) => acc + arr.length, 0);
        if (floorCount === 0) return null;
        const expanded = expandedBuildings[b.building_id] ?? true;
        const floorList = floorsByBuilding?.[b.building_id] || [];
        return (
          <div
            key={b.building_id}
            className="rounded-xl border"
            style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}
            data-testid={`tree-building-${b.building_id}`}
          >
            <button
              onClick={() => toggleBuilding(b.building_id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left"
            >
              {expanded ? (
                <ChevronDown className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.5)' }} />
              ) : (
                <ChevronRight className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.5)' }} />
              )}
              <Building2 className="w-4 h-4" style={{ color: '#D9A05B' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--c-text-1, #fff)' }}>{b.name}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full tabular-nums ml-auto"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)' }}>
                {floorCount}
              </span>
            </button>
            {expanded && (
              <div className="px-3 pb-2 space-y-2">
                {Object.entries(byFloor).map(([floorId, filesArr]) => {
                  const floor = floorList.find((f) => f.floor_id === floorId);
                  const floorName = floor?.name || (floorId === '__unfloored__' ? 'Sans étage' : floorId);
                  return (
                    <div key={floorId} className="pl-5 space-y-1.5">
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider"
                        style={{ color: 'rgba(255,255,255,0.45)' }}>
                        <Layers className="w-3 h-3" />
                        {floorName}
                        <span className="opacity-60">· {filesArr.length}</span>
                      </div>
                      <div className="space-y-1.5">
                        {filesArr.map((f) => renderRow(f))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------- File row ----------
const FileRow = ({ file, allLots, buildings, floorsByBuilding, onUpdate, onRecategorize, onDelete, onOpenCropper, onApplySuggestion }) => {
  const meta = DOC_TYPE_META[file.doc_type] || DOC_TYPE_META.other;
  const fields = fieldsFor(file.doc_type);
  const sug = file.suggestion;
  const sugLabel = sug ? [sug.doc_type && `→ ${DOC_TYPE_META[sug.doc_type]?.label}`, sug.building_id && buildings.find((b) => b.building_id === sug.building_id)?.name, sug.floor_id && (floorsByBuilding?.[sug.building_id || file.building_id] || []).find((fl) => fl.floor_id === sug.floor_id)?.name, sug.lot_id && allLots.find((l) => l.lot_id === sug.lot_id)?.name].filter(Boolean).join(' · ') : '';
  const Icon = meta.icon;
  const tone = confidenceTone(file.confidence_score);
  const status = file.categorization_status;
  const isProcessing = status === 'queued' || status === 'processing';
  const isImage = file.content_type?.startsWith('image/');

  const downloadFile = async () => {
    try {
      const response = await axios.get(`${API}/files/${file.file_id}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      a.click();
      a.remove();
    } catch {
      toast.error('Erreur téléchargement');
    }
  };

  const floorList = floorsByBuilding?.[file.building_id] || [];

  return (
    <div
      className="flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors"
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
      data-testid={`file-row-${file.file_id}`}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/file-id', file.file_id); e.dataTransfer.effectAllowed = 'move'; }}
    >
      {/* Icon */}
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}55` }}
      >
        <Icon className="w-4 h-4" style={{ color: meta.color }} />
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-medium truncate cursor-help"
          style={{ color: 'var(--c-text-1, #fff)' }}
          title={file.original_filename}
          data-testid={`file-name-${file.file_id}`}
        >
          {file.original_filename}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {file.size ? `${(file.size / 1024).toFixed(0)} Ko` : ''}
          </span>
          {file.categorization_source === 'ai' && (
            <span className="text-[10px] flex items-center gap-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
              <Sparkles className="w-2.5 h-2.5" />
              IA
            </span>
          )}
          {file.doc_type_overridden && (
            <span className="text-[10px]" style={{ color: '#D9A05B' }}>· corrigé</span>
          )}
          {!fields.length && TYPE_HINT[file.doc_type] && <span className="text-[10px] text-[var(--c-text-faint)]">· {TYPE_HINT[file.doc_type]}</span>}
        </div>
      </div>
      {sug && sugLabel && (
        <button type="button" onClick={() => onApplySuggestion(file)} title={sug.reason || ''} data-testid={`apply-suggestion-${file.file_id}`}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[10.5px] font-medium bg-[var(--c-gold-soft)] text-[var(--c-gold)] border border-[var(--c-border-accent)] hover:bg-[var(--c-gold)] hover:text-[var(--c-on-gold)] transition-colors max-w-[260px]">
          <Sparkles className="w-3 h-3 shrink-0" /><span className="truncate">Appliquer : {sugLabel}</span>
        </button>
      )}

      {/* Confidence */}
      {isProcessing ? (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px]"
          style={{ background: 'rgba(217,160,91,0.10)', color: '#D9A05B' }}>
          <Loader2 className="w-3 h-3 animate-spin" />
          IA…
        </div>
      ) : (
        file.confidence_score != null && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] tabular-nums"
            style={{ background: `${tone.color}18`, color: tone.color, border: `1px solid ${tone.color}40` }}
            title={file.categorization_reasoning || ''}
            data-testid={`confidence-${file.file_id}`}>
            {tone.label}
          </div>
        )
      )}

      {/* Doc type override */}
      <Select
        value={file.doc_type || 'other'}
        onValueChange={(v) => onUpdate(file.file_id, { doc_type: v })}
      >
        <SelectTrigger
          className="w-32 h-8 text-xs"
          data-testid={`doc-type-${file.file_id}`}
          title={DOC_TYPE_META[file.doc_type || 'other']?.label || 'Type document'}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DOC_TYPES_LIST.map((dt) => (
            <SelectItem key={dt} value={dt}>{DOC_TYPE_META[dt].label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Building / Floor / Lot quick assign — selon le type */}
      {fields.includes('building') && <Select
        value={file.building_id || 'none'}
        onValueChange={(v) => onUpdate(file.file_id, { building_id: v === 'none' ? null : v, floor_id: null })}
      >
        <SelectTrigger
          className="w-28 h-8 text-xs"
          data-testid={`building-assign-${file.file_id}`}
          title={buildings.find((b) => b.building_id === file.building_id)?.name || 'Bâtiment (non assigné)'}
        >
          <SelectValue placeholder="Bâtiment" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Aucun</SelectItem>
          {buildings.map((b) => (
            <SelectItem key={b.building_id} value={b.building_id} title={b.name}>{b.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>}

      {fields.includes('floor') && file.building_id && floorList.length > 0 && (
        <Select
          value={file.floor_id || 'none'}
          onValueChange={(v) => onUpdate(file.file_id, { floor_id: v === 'none' ? null : v })}
        >
          <SelectTrigger
            className="w-24 h-8 text-xs"
            data-testid={`floor-assign-${file.file_id}`}
            title={floorList.find((fl) => fl.floor_id === file.floor_id)?.name || 'Étage (non assigné)'}
          >
            <SelectValue placeholder="Étage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">—</SelectItem>
            {floorList.map((fl) => (
              <SelectItem key={fl.floor_id} value={fl.floor_id} title={fl.name}>{fl.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Lot assign (plans de lot image) */}
      {fields.includes('lot') && isImage && (
        <Select
          value={allLots.find((l) => l.selected_plan_file_id === file.file_id)?.lot_id || 'none'}
          onValueChange={async (lotId) => {
            try {
              const prevLot = allLots.find((l) => l.selected_plan_file_id === file.file_id);
              if (prevLot) {
                await axios.put(`${API}/lots/${prevLot.lot_id}`, { selected_plan_file_id: '' }, { withCredentials: true });
              }
              if (lotId !== 'none') {
                await axios.put(`${API}/lots/${lotId}`, { selected_plan_file_id: file.file_id }, { withCredentials: true });
              }
              toast.success(lotId !== 'none' ? 'Plan assigné au lot' : 'Plan détaché');
            } catch {
              toast.error('Erreur assignation');
            }
          }}
        >
          <SelectTrigger
            className="w-32 h-8 text-xs"
            data-testid={`lot-assign-${file.file_id}`}
            title={(() => {
              const lot = allLots.find((l) => l.selected_plan_file_id === file.file_id);
              return lot ? `${lot.lot_number || ''} ${lot.name ? '· ' + lot.name : ''} ${lot.typology ? '(' + lot.typology + ')' : ''}`.trim() : 'Aucun lot assigné';
            })()}
          >
            <SelectValue placeholder="Lot" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Aucun lot</SelectItem>
            {allLots.map((l) => (
              <SelectItem
                key={l.lot_id}
                value={l.lot_id}
                title={`${l.lot_number || ''} ${l.name ? '· ' + l.name : ''} ${l.typology ? '(' + l.typology + ')' : ''}`.trim()}
              >
                {l.lot_number || l.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Actions */}
      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onUpdate(file.file_id, { is_retained: !file.is_retained }, { silent: true })}
          title={(() => {
            const isClientRef = isImage && ['ref_visuel', 'ref_materiau', 'ref_mobilier', 'inspiration'].includes(file.doc_type);
            if (isClientRef) {
              return file.is_retained
                ? 'Retenue ⭐ — injectée dans les rendus 3D (max 5 plus récentes par projet). Cliquer pour retirer.'
                : 'Retenir ⭐ — sera injectée comme image de référence dans la prochaine génération 3D';
            }
            if (isImage) {
              return file.is_retained
                ? 'Retenue — utilisée par la brochure commerciale. Pour la génération 3D, catégorisez-la en Réf. mobilier / matériau / visuel / inspiration.'
                : 'Retenir ce fichier (brochure commerciale). Pour la génération 3D, catégorisez d\'abord en Réf. mobilier / matériau / visuel / inspiration.';
            }
            return file.is_retained ? 'Retirer des retenus' : 'Marquer comme retenu';
          })()}
          data-testid={`star-${file.file_id}`}
          className="h-8 w-8"
        >
          <Star
            className="w-4 h-4"
            fill={file.is_retained ? '#D9A05B' : 'none'}
            style={{ color: file.is_retained ? '#D9A05B' : 'rgba(255,255,255,0.4)' }}
          />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onRecategorize(file.file_id)}
          title="Relancer l'IA"
          data-testid={`recategorize-${file.file_id}`}
          className="h-8 w-8"
          disabled={isProcessing}
        >
          <Wand2 className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.5)' }} />
        </Button>
        {/* Asset Pipeline — bouton Recadrer (visible si le fichier est classé "plan_lot") */}
        {file.doc_type === 'plan_lot' && onOpenCropper && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenCropper(file)}
            title="Recadrer / Éditer le plan"
            data-testid={`crop-${file.file_id}`}
            className="h-8 w-8 hover:bg-amber-500/10"
          >
            <Crop className="w-4 h-4" style={{ color: 'var(--c-gold, #D9A05B)' }} />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={downloadFile}
          title="Télécharger"
          data-testid={`download-${file.file_id}`}
          className="h-8 w-8"
        >
          <Download className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.5)' }} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(file)}
          title="Supprimer"
          data-testid={`delete-${file.file_id}`}
          className="h-8 w-8 hover:bg-red-500/10"
        >
          <Trash2 className="w-4 h-4 text-red-400" />
        </Button>
      </div>
    </div>
  );
};

// ---------- Generations grid ----------
const GenerationsGrid = ({ generations, loading, allLots, buildings, projectId, onOpenStudio }) => {
  const goToStudio = () => {
    if (typeof onOpenStudio === 'function') onOpenStudio();
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#D9A05B' }} />
      </div>
    );
  }
  if (!generations || generations.length === 0) {
    return (
      <div>
        <EmptyState icon={Sparkles} label="Aucune génération" sub="Lance une shoebox ou une vue FPS depuis le Studio du projet" />
        <div className="flex justify-center mt-3">
          <Button
            onClick={goToStudio}
            data-testid="goto-studio-empty"
            style={{ background: '#D9A05B', color: '#000' }}
            className="hover:opacity-90"
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            Ouvrir le Studio
          </Button>
        </div>
      </div>
    );
  }
  // Group by building (via lot.building_id from allLots lookup)
  const lotById = Object.fromEntries(allLots.map((l) => [l.lot_id, l]));
  const groups = {};
  for (const g of generations) {
    const lot = lotById[g.lot_id];
    const bId = lot?.building_id || '__nobuilding__';
    if (!groups[bId]) groups[bId] = [];
    groups[bId].push(g);
  }
  const buildingName = (bId) =>
    bId === '__nobuilding__' ? 'Sans bâtiment' : (buildings.find((b) => b.building_id === bId)?.name || bId);

  return (
    <div className="space-y-4" data-testid="generations-grid">
      {Object.entries(groups).map(([bId, items]) => (
        <div key={bId}>
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4" style={{ color: '#D9A05B' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--c-text-1, #fff)' }}>{buildingName(bId)}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)' }}>
              {items.length}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {items.map((g) => {
              const lot = lotById[g.lot_id];
              const isFps = g.view_type === 'fps';
              return (
                <div
                  key={g.job_id}
                  className="group rounded-xl overflow-hidden border transition-all"
                  style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}
                  data-testid={`gen-card-${g.job_id}`}
                >
                  <div className="aspect-square overflow-hidden relative" style={{ background: 'rgba(0,0,0,0.4)' }}>
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${g.image_url}`}
                      alt={`Génération ${g.job_id}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <span
                      className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider"
                      style={{
                        background: isFps ? 'rgba(20,184,166,0.85)' : 'rgba(217,160,91,0.85)',
                        color: '#000',
                      }}
                    >
                      {isFps ? `FPS · ${g.fov_deg || 75}°` : 'Shoebox'}
                    </span>
                  </div>
                  <div className="px-2.5 py-2">
                    <p
                      className="text-xs font-medium truncate"
                      style={{ color: 'var(--c-text-1, #fff)' }}
                      title={`${lot?.name || ''} (${lot?.lot_number || ''}) · ${lot?.typology || ''}`.trim()}
                    >
                      {lot?.lot_number || lot?.name || 'Lot'} · {lot?.typology || '—'}
                    </p>
                    <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                      {new Date(g.completed_at || g.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------- Empty state ----------
const EmptyState = ({ icon: Icon, label, sub }) => (
  <div
    className="text-center py-10 rounded-xl"
    style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px dashed rgba(255,255,255,0.08)',
    }}
  >
    <Icon className="w-10 h-10 mx-auto mb-3" style={{ color: 'rgba(255,255,255,0.25)' }} />
    <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>{label}</p>
    {sub && <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{sub}</p>}
  </div>
);

export default ProjectFilesWorkspace;
